<template>
  <el-card shadow="never" v-loading="loading">
    <div v-if="shouldShowProgress" class="transfer-progress">
      <el-alert
        :title="transferProgress.message"
        type="info"
        :closable="false"
        show-icon
        :show-close="false"
        class="progress-alert"
      >
        <div class="progress-content">
          <el-progress
            :percentage="transferProgressPercentage"
            :stroke-width="8"
            :show-text="false"
            status="success"
          />
          <span class="progress-text">
            {{ transferProgress.current }} of {{ transferProgress.total }} stages created
          </span>
        </div>
      </el-alert>
    </div>
    <div v-if="shouldShowSuccessMessage" class="transfer-success">
      <el-alert
        title="Transfer Creation Complete"
        type="success"
        :closable="false"
        show-icon
        :show-close="false"
      >
        All transfer stages have been created successfully. You can now manage agreements and view line items.
      </el-alert>
    </div>

    <el-empty v-if="!leaseTransfer || !leaseTransfer.stages || leaseTransfer.stages.length === 0" 
      :description="'No active lease transfer found.'">
      <el-button type="primary" size="small" @click="$emit('reload')">
        Refresh
      </el-button>
    </el-empty>
    <template v-if="leaseTransfer && leaseTransfer.stages && leaseTransfer.stages.length > 0">
      <div v-if="hasNextStage" class="next-stage-info">
        <h6>Next Stage Information</h6>
        <div class="next-stage-details">
          <div class="amount-breakdown">
            <div class="breakdown-item">
              <span class="label">Amount Due to Start Next Stage:</span>
              <span class="value amount-due">{{ formatCurrency(amountPayableForNextStage) }}</span>
            </div>
            <div class="breakdown-item">
              <span class="label">Transferable from Current Lease:</span>
              <span class="value transferable">{{
                formatCurrency(amountTransferable)
              }}
              </span>
            </div>
            <div class="breakdown-item" v-if="achPending > 0">
              <span class="label">ACH Pending:</span>
              <span class="value ach-pending">{{
                formatCurrency(achPending)
              }}
              </span>
            </div>
            <div class="breakdown-item">
              <span class="label">Available Wallet Balance:</span>
              <span class="value wallet-balance">{{ formatCurrency(walletBalance) }}</span>
            </div>
            <div class="breakdown-item total">
              <span class="label">Actual Amount to Pay:</span>
              <span class="value actual-amount">{{ formatCurrency(actualAmountToPay) }}</span>
            </div>
          </div>
          
          <div class="next-stage-summary">
            <el-alert
              :title="`Next Stage: ${activeStage.step}`"
              :type="actualAmountToPay > 0 ? 'warning' : 'success'"
              :closable="false"
              show-icon
              :show-close="false"
            >
              <span v-if="actualAmountToPay > 0">
                Customer needs to pay <strong>{{ formatCurrency(actualAmountToPay) }}</strong> for the next stage.
              </span>
              <span v-else>
                No additional payment required! Transferable amounts, wallet balance{{ achPending > 0 ? ' and ACH pending' : '' }} cover the next stage completely.
              </span>
            </el-alert>
            
            <div v-if="(amountTransferable < amountPayableForNextStage && walletBalance > 0) || achPending > 0" class="wallet-info">
              <el-alert
                title="Available Funds Applied"
                type="info"
                :closable="false"
                show-icon
                :show-close="false"
                size="small"
              >
                <div v-if="walletBalance > 0 && amountTransferable < amountPayableForNextStage">
                  <span>
                    Customer's wallet balance of <strong>{{ formatCurrency(walletBalance) }}</strong> will be automatically applied to reduce the amount due.
                  </span>
                </div>
                <div v-if="achPending > 0">
                  <span>
                    ACH pending amount of <strong>{{ formatCurrency(achPending) }}</strong> will be automatically applied to reduce the amount due.
                  </span>
                </div>
              </el-alert>
            </div>
          </div>
        </div>
      </div>
      
      <el-collapse v-if="leaseTransfer && leaseTransfer.stages && leaseTransfer.stages.length">
        <el-collapse-item :key="stage.step" v-for="stage of leaseTransfer.stages" :title="`Stage: ${stage.step}`">
          <template slot="title">
            <div class="stage-title-content">
              <div class="stage-basic-info">
                <span>
                  {{  `Stage ${stageLabel(stage.step)}:` }} Start: {{ stage.start | formatDate }}
                </span>
                <span v-if="stage.end">&nbsp; End: {{ stage.end | formatDate }}</span>
                <span>&nbsp; Status: {{  stage.status }}</span>
              </div>
              <div v-if="stage.active && stage.step < leaseTransfer.transfer.stages" class="amount-transferable">
                <el-tag type="success" size="small">
                  Transferable: {{ formatCurrency(amountTransferable) }}
                </el-tag>
              </div>
              <div v-if="stage.step === activeStage.step + 1" class="amount-transferable">
                <el-tag type="warning" size="small">
                  Due Amount: {{ amountPayableForNextStage | currencySign }}
                </el-tag>
              </div>
            </div>
          </template>
          <el-card>
            <div class="stage-actions">
              <el-popconfirm
                title="Are you sure to delete this stage?"
                @confirm="() => removeStage(stage)"
                confirm-button-text="Yes, Cancel"
                cancel-button-text="No, Keep"
                confirm-button-type="danger"
                cancel-button-type="primary"
                icon="el-icon-warning"
                icon-color="#f56c6c"
              >
                <el-button 
                  slot="reference" 
                  v-if="leaseTransfer.stages[0].completed && stage.step === leaseTransfer.stages.length && !stage.active"
                  type="danger"
                  size="small"
                  icon="el-icon-delete"
                  class="cancel-stage-btn"
                >
                  Cancel Stage
                </el-button>
              </el-popconfirm>
              <!-- Agreement Management Group -->
              <div class="agreement-group">
                <span class="group-label">Agreement:</span>
                <el-button v-if="stage.step > 1 && !stage.lease.agreementOnS3" size="small" icon="el-icon-eye"
                  @click="() => {
                    selectedStageForSigners = stage;
                    getSignersStatus(stage.lease);
                    signersStatusDialog = true;
                  }">
                  View
                </el-button>
                <el-button v-if="stage.step > 1 && !(stage.lease && stage.lease.agreement)" size="small" type="primary"
                  :disabled="loading" @click="() => openLicenseAgreementDialog(stage.lease)">
                  Send
                </el-button>
                <el-button v-if="stage.step > 1 && stage.lease && stage.lease.agreement && !stage.lease.agreementOnS3 && agreementStatus(stage.lease) !== 'Completed'"
                  size="small" type="danger"
                  @click="() => cancelLicenseAgreement(stage.lease)">
                  Cancel
                </el-button>
                <el-button v-if="stage.lease.agreementSigned" size="small" type="success"
                  icon="el-icon-download"
                  @click="downloadAgreement(stage.lease)">
                  Download
                </el-button>
                <el-button v-if="stage.step > 1 && stage.lease && !stage.lease.agreementSigned && stage.lease.agreement" 
                  size="small" type="info"
                  icon="el-icon-bell"
                  @click="() => sendReminderEmail(stage.lease)">
                  Remind
                </el-button>
              </div>
              
              <div class="custom-agreement-group" v-if="stage.step > 1">
                <span class="group-label">Custom:</span>
                <Uploader
                  v-if="allowAccess('level_1') && stage.step > 1"
                  button-text="Upload"
                  button-size="small"
                  button-type="warning"
                  @upload-file="response => addCustomerAgreement(stage.lease._id, response)"
                  no-tip
                  :upload-prefix="`customer/${stage.customer}/`"
                />
              </div>
            </div>
            <CustomerLeaseLineItems
              :customer="leaseTransfer.customer"
              :invoice="{ lineItems: stage.lease.lineItems, _id: stage.lease._id }"
              :getMonthValue="getMonthValue"
              :lineItemBackgroundColor="lineItemBackgroundColor"
              :hasVoidAction="false"
              />
          </el-card>
        </el-collapse-item>
      </el-collapse>
    </template>
    <el-dialog :visible.sync="licenseAgreementDialog" destroy-on-close width="75%">
      <CustomerRentalAgreement
        v-if="leaseSelectedForAgreement"
        :customer-id="leaseSelectedForAgreement.customerId"
        :move-in="new Date(leaseSelectedForAgreement.start)"
        :move-out="new Date(leaseSelectedForAgreement.end)"
        :lease-price="leasePrice"
        :prorated-rent="computedProratedPrice"
        :deposit="totalDeposit"
        :facility="leaseSelectedForAgreement.facility"
        :units="leaseSelectedForAgreement.units"
        units-have-only-ids
        @confirmed="agreementConfirmed"
        :lease-id="leaseSelectedForAgreement._id"
      />
    </el-dialog>

    <el-dialog
      :visible.sync="signersStatusDialog"
      destroy-on-close
      width="80%"
      :title="`Signers Status - Stage ${selectedStageForSigners && selectedStageForSigners.step - 1 || ''}`"
      :close-on-click-modal="false"
      class="signers-dialog"
    >
      <div v-if="signersStatusLoading" class="loading-container">
        <el-skeleton :rows="3" animated />
      </div>

      <div v-else-if="signersStatus.length" class="signers-content">
        <!-- Signers Table -->
        <div class="signers-table-section">
          <el-card shadow="never" class="table-card">
            <div slot="header" class="table-header">
              <div class="header-left">
                <i class="el-icon-user"></i>
                <span>Signers Details</span>
                <el-tag v-if="signersStatus.length" size="mini" type="info">
                  {{ signersStatus.length }} signer{{ signersStatus.length > 1 ? 's' : '' }}
                </el-tag>
              </div>
              <el-button
                size="mini"
                icon="el-icon-refresh"
                @click="refreshSignersStatus"
                :loading="signersStatusLoading"
                class="refresh-btn"
              >
                Refresh
              </el-button>
            </div>

            <el-table
              :data="signersStatus"
              v-loading="signersStatusLoading"
              class="signers-table"
              :row-class-name="getRowClassName"
            >
              <el-table-column label="Signer" min-width="180">
                <template slot-scope="scope">
                  <div class="signer-info">
                    <div class="signer-name">
                      <i :class="getSignerIcon(scope.row.signActionType)"></i>
                      {{ scope.row.name }}
                    </div>
                    <div class="signer-email">{{ scope.row.email }}</div>
                  </div>
                </template>
              </el-table-column>

              <el-table-column label="Type" width="120" align="center">
                <template slot-scope="scope">
                  <el-tag :type="getSignerTypeTag(scope.row.signActionType)" size="small">
                    {{ scope.row.signActionType }}
                  </el-tag>
                </template>
              </el-table-column>

              <el-table-column label="Status" width="140" align="center">
                <template slot-scope="scope">
                  <el-tag
                    :type="getSignerStatusType(scope.row.status)"
                    size="medium"
                    effect="dark"
                  >
                    <i :class="getStatusIcon(scope.row.status)"></i>
                    {{ formatStatus(scope.row.status) }}
                  </el-tag>
                </template>
              </el-table-column>

              <el-table-column label="Actions" width="140" align="center">
                <template slot-scope="scope">
                  <el-button
                    v-if="scope.row.status !== 'completed'"
                    size="mini"
                    type="warning"
                    icon="el-icon-bell"
                    @click="sendReminderToSigner(scope.row)"
                    :loading="scope.row.sendingReminder"
                    :disabled="scope.row.status === 'declined'"
                  >
                    {{ scope.row.status === 'declined' ? 'Declined' : 'Remind' }}
                  </el-button>
                  <el-tag v-else type="success" size="mini" effect="plain">
                    <i class="el-icon-check"></i>
                    Completed
                  </el-tag>
                </template>
              </el-table-column>
            </el-table>
          </el-card>
        </div>
      </div>

      <div v-else class="no-signers">
        <el-empty description="No signers found for this agreement">
          <el-button type="primary" size="small" @click="refreshSignersStatus" :loading="signersStatusLoading">
            <i class="el-icon-refresh"></i>
            Refresh Status
          </el-button>
        </el-empty>
      </div>

      <el-button @click="signersStatusDialog = false">
        <i class="el-icon-close"></i>
        Close
      </el-button>
      <div slot="footer" class="dialog-footer">
      </div>
    </el-dialog>
  </el-card>
</template>

<script>
import axios from 'axios';
import CustomerLeaseLineItems from './CustomerLeaseLineItems';
import CustomerRentalAgreement from './CustomerRentalAgreement';
import Uploader from './Uploader';
import getMonth from '../utils/timing';
import { allowAccess } from '../utils/roles';
import RsAnvilApi from '../api/rsAnvilApi';
import { UNIT_RENTAL_API_BASE, LEASING_API, FACILITY_ACCOUNT_API_BASE, UPLOAD_API, FACILITY_TRANSACTIONS_API_BASE, LEASE_TRANSFER_API } from '../env';

export default {
  name: 'LeaseTransferView',
  props: {
    leaseTransfer: {
      type: Object,
      required: true,
    },
    facility: { type: String, required: true },
    customer: { type: String, required: true },
  },
  data() {
    return {
      loading: false,
    };
  },
  components: {
    CustomerLeaseLineItems,
    CustomerRentalAgreement,
    Uploader,
  },
  async mounted() {
    this.loading = true;
    await this.getFacilityAccount();
    await this.getACHPendingTransactions();
    if (!this.leaseTransfer) {
      this.loading = false;
      return;
    }

    this.leaseTransfer.stages.forEach((stage) => {
      this.getUnitRentals(stage.lease._id);
    });
    
    if (this.leaseTransfer.transfer && this.leaseTransfer.stages) {
      const expectedStages = this.leaseTransfer.transfer.stages;
      const actualStages = this.leaseTransfer.stages.length;

      if (expectedStages > actualStages) {
        this.wasJustCompleted = false;
        // Parent component will handle polling
      } else {
        this.transferCreationStatus = 'ready';
        this.transferProgress.current = actualStages;
        this.transferProgress.total = actualStages;
        this.transferProgress.message = 'Transfer creation complete';
        this.wasJustCompleted = false;
      }
    }

    this.loading = false;

    await this.downloadRentalAgreements();
  },
  created() {
    this.$parent.$on('transfer-cancelled', this.handleTransferCancelled);
  },
  beforeDestroy() {
    this.$parent.$off('transfer-cancelled', this.handleTransferCancelled);
  },
  watch: {
    customer() {
      this.getFacilityAccount();
      this.getACHPendingTransactions();
    },
    facility() {
      this.getFacilityAccount();
      this.getACHPendingTransactions();
    },
    leaseTransfer: {
      handler(newVal, oldVal) {
        // Handle case when leaseTransfer becomes null (cancelled)
        if (!newVal && oldVal) {
          this.handleTransferCancellation();
          return;
        }

        if (newVal && newVal.stages) {
          this.unitRentals = [];
          newVal.stages.forEach((stage) => {
            this.getUnitRentals(stage.lease._id);
          });
        }
        if (oldVal && oldVal.transfer && !newVal) {
          this.cleanupTransferState();
        }
        if (!newVal) {
          this.cleanupTransferState();
          return;
        }
        if (newVal && newVal.transfer && newVal.stages) {
          const expectedStages = newVal.transfer.stages;
          const actualStages = newVal.stages.length;
          if (expectedStages > actualStages) {
            this.wasJustCompleted = false;
            // Parent component will handle polling
          } else {
            this.transferCreationStatus = 'ready';
            this.transferProgress.current = actualStages;
            this.transferProgress.total = actualStages;
            this.transferProgress.message = 'Transfer creation complete';
            this.wasJustCompleted = false;
          }
        }
      },
      deep: true,
      immediate: true,
    },
  },
  data() {
    return {
      loading: false,
      unitRentals: [],
      licenseAgreementDialog: false,
      signersStatusDialog: false,
      signersStatus: [],
      signersStatusLoading: false,
      selectedStageForSigners: null,
      leaseSelectedForAgreement: null,
      transferCreationStatus: 'idle', // 'idle', 'creating', 'ready', 'error'
      wasJustCompleted: false,
      facilityAccount: null,
      achPending: 0,
      transferProgress: {
        current: 0,
        total: 0,
        message: '',
      },
    };
  },
  computed: {
    walletBalance() {
      return this.facilityAccount ? this.facilityAccount.balanceAmount : 0;
    },
    activeStage() {
      if (!this.leaseTransfer || !this.leaseTransfer.stages) return null;
      const activeStage = this.leaseTransfer.stages.find(stage => stage.active);
      return activeStage;
    },
    hasNextStage() {
      if (!this.activeStage) return false;
      return this.activeStage.step < this.leaseTransfer.stages.length;
    },
    isTransferCreating() {
      return this.transferCreationStatus === 'creating';
    },
    isTransferReady() {
      return this.transferCreationStatus === 'ready';
    },
    shouldShowProgress() {
      if (!this.leaseTransfer || !this.leaseTransfer.stages) return false;
      
      const expectedStages = this.leaseTransfer.transfer.stages;
      const actualStages = this.leaseTransfer.stages.length;
      
      return actualStages < expectedStages;
    },
    shouldShowSuccessMessage() {
      return this.isTransferReady && !this.isTransferCreating && this.wasJustCompleted;
    },
    transferProgressPercentage() {
      if (this.transferProgress.total === 0) return 0;
      return Math.round((this.transferProgress.current / this.transferProgress.total) * 100);
    },
    amountPayableForNextStage() {
      if (
        !this.hasNextStage || !this.activeStage || !this.leaseTransfer || !this.leaseTransfer.stages
      ) {
        return 0;
      }

      const nextStage = this.leaseTransfer.stages
        .find(stage => stage.step === this.activeStage.step + 1);

      return nextStage && nextStage.lease ? nextStage.lease.amount : 0;
    },
    amountTransferable() {
      if (!this.leaseTransfer || !this.leaseTransfer.stages) return 0;

      // const activeStage = this.leaseTransfer.stages.find(stage => stage.active);
      const activeStage = this.leaseTransfer.stages[this.leaseTransfer.transfer.activeStage - 1];
      if (!activeStage || !activeStage.lease) return 0;

      const overpayment = activeStage.lease.amountPaid > activeStage.lease.amount
        ? activeStage.lease.amountPaid - activeStage.lease.amount
        : 0;
      const amountDue = activeStage.lease.amount > activeStage.lease.amountPaid
        ? activeStage.lease.amount - activeStage.lease.amountPaid
        : 0;

      const depositPaid = activeStage.lease.depositPaid || 0;

      return Math.round((overpayment + (depositPaid - amountDue)) * 100) / 100;
    },
    actualAmountToPay() {
      if (!this.hasNextStage || !this.amountPayableForNextStage) return 0;

      const transferableAmount = this.amountTransferable;
      const walletBalanceAmount = this.walletBalance;
      const achPendingAmount = this.achPending;
      const nextStageAmount = this.amountPayableForNextStage;

      const totalAvailableFunds = transferableAmount + walletBalanceAmount + achPendingAmount;

      return Math.max(0, nextStageAmount - totalAvailableFunds);
    },
    totalDeposit() {
      let depositItems = 0;
      this.leaseSelectedForAgreement.lineItems.forEach((item) => {
        if (item.details && item.details === 'DEPOSIT') {
          depositItems += item.amount;
        }
      });
      return `${this.$options.filters.currencySign(Number(depositItems), this.$root.state.facilityData)}`;
    },
    leasePrice() {
      let price = 0;
      this.unitRentals.forEach((rental) => {
        if (rental.unitInvoice === this.leaseSelectedForAgreement._id) {
          price += rental.updatedPrice ? rental.updatedPrice : rental.pricing.price;
        }
      });

      return price;
    },
    computedProratedPrice() {
      let rentItems = 0;
      this.leaseSelectedForAgreement.lineItems.forEach((item) => {
        if (item.chargeType === 'RENT') {
          rentItems += item.amount;
        }
      });
      return rentItems;
    },
  },
  methods: {
    stageLabel(stage) {
      if (stage === 1) {
        return 'Current';
      } else if (this.leaseTransfer.stages.length > 2 && stage === 2) {
        return 'Interim';
      }
      return 'Final';
    },
    async downloadRentalAgreements() {
      const stages = this.leaseTransfer.stages;
      for (let i = 0; i < stages.length; i++) {
        const stage = stages[i];
        const lease = stage.lease;
        if (lease && !lease.agreementOnS3 && lease.agreement) {
          await this.getSignersStatus(lease);
          if (lease.signers[0].status === 'completed' && lease.signers[1].status === 'completed' && !lease.agreementSigned && !lease.agreementOnS3) {
            await this.saveAgreement(lease);
          }
        }
      }

      this.$emit('reload');
    },
    async saveAgreement(lease) {
      this.managerSignerDialog = false;
      const anvilClient = new RsAnvilApi(this.$keycloak.token);
      const etchPacket = JSON.parse(lease.agreement);
      const documentEid = etchPacket.documentGroup.eid;
      const agreementBuffer = await anvilClient.downloadAgreement(documentEid);

      const formData = new FormData();
      const buffer = new Uint8Array(agreementBuffer.data.data);
      const blob = new Blob([buffer], { type: 'application/pdf' });
      formData.append('document', blob, `${documentEid}.pdf`);

      const response = await axios.post(UPLOAD_API, formData, {
        headers: {
          Authorization: `Bearer ${this.$keycloak.token}`,
          'Content-Type': 'multipart/form-data',
        },
      });

      await axios.put(
        `${LEASING_API}/v1/${this.facility}/${lease._id}`,
        {
          agreementSigned: true,
          agreementOnS3: response.data.url,
        },
        {
          headers: {
            Authorization: `Bearer ${this.$keycloak.token}`,
          },
        },
      );
    },
    async getACHPendingTransactions() {
      const { data } = await axios.get(`${FACILITY_TRANSACTIONS_API_BASE}/v1/${this.facility}/${this.customer}/pending/ach`, {
        headers: {
          Authorization: `Bearer ${this.$keycloak.token}`,
        },
      });

      this.achPending = data.result.transactions
        .filter(transaction => transaction.events && transaction.events[transaction.events.length - 1].status !== 'failed')
        .map(item => item.amount).reduce((pv, nx) => pv + nx, 0);
      return this.achPending;
    },
    async getFacilityAccount() {
      const response = await axios.get(`${FACILITY_ACCOUNT_API_BASE}/v1/${this.facility}/${this.customer}`, {
        headers: {
          Authorization: `Bearer ${this.$keycloak.token}`,
        },
      });
      this.facilityAccount = response.data;
    },
    updateTransferProgress(progress) {
      this.transferProgress = { ...progress };
      this.transferCreationStatus = progress.status || 'creating';
      this.wasJustCompleted = progress.wasJustCompleted || false;
    },
    handleTransferCancellation() {
      this.transferCreationStatus = 'idle';
      this.transferProgress.current = 0;
      this.transferProgress.total = 0;
      this.transferProgress.message = 'Transfer was cancelled';
      this.wasJustCompleted = false;
      this.unitRentals = [];
      console.log('Transfer cancellation state updated');
    },

    getAmountTransferable(stage) {
      if (!stage || !stage.lease) return 0;

      const overpayment = stage.lease.amountPaid > stage.lease.amount
        ? stage.lease.amountPaid - stage.lease.amount
        : 0;
      const depositPaid = stage.lease.depositPaid || 0;

      return Math.round((overpayment + depositPaid) * 100) / 100;
    },
    getAgreementStatusType(lease) {
      const status = this.agreementStatus(lease);
      if (status === 'Completed') return 'success';
      if (status === 'Waiting for customer') return 'warning';
      if (status === 'Waiting for manager') return 'info';
      return 'info';
    },
    getSignerStatusType(status) {
      if (status === 'completed') return 'success';
      if (status === 'pending') return 'warning';
      if (status === 'declined') return 'danger';
      if (status === 'sent') return 'info';
      return 'info';
    },
    getSignerIcon(signActionType) {
      if (signActionType === 'Customer') return 'el-icon-user';
      if (signActionType === 'Manager') return 'el-icon-s-custom';
      return 'el-icon-user-solid';
    },
    getSignerTypeTag(signActionType) {
      if (signActionType === 'Customer') return 'primary';
      if (signActionType === 'Manager') return 'success';
      return 'info';
    },
    getStatusIcon(status) {
      if (status === 'completed') return 'el-icon-check';
      if (status === 'pending') return 'el-icon-time';
      if (status === 'declined') return 'el-icon-close';
      if (status === 'sent') return 'el-icon-s-promotion';
      return 'el-icon-info';
    },
    formatStatus(status) {
      return status.charAt(0).toUpperCase() + status.slice(1);
    },
    formatDate(dateString) {
      if (!dateString) return '-';
      return new Date(dateString).toLocaleDateString('en-US', {
        year: 'numeric',
        month: 'short',
        day: 'numeric',
        hour: '2-digit',
        minute: '2-digit',
      });
    },
    getRowClassName({ row }) {
      if (row.status === 'completed') return 'completed-row';
      if (row.status === 'pending') return 'pending-row';
      if (row.status === 'declined') return 'declined-row';
      return '';
    },
    async refreshSignersStatus() {
      if (this.selectedStageForSigners) {
        await this.getSignersStatus(this.selectedStageForSigners.lease);
      }
    },
    async sendReminderToSigner(signer) {
      try {
        this.$set(signer, 'sendingReminder', true);
        const agreementString = this.selectedStageForSigners.lease.agreement;
        const agreement = JSON.parse(agreementString);
        const rsAnvilClient = new RsAnvilApi(this.$keycloak.token);

        await rsAnvilClient.sendReminderToSigners(agreement.eid);

        this.$message.success({
          message: `Reminder sent to ${signer.name}`,
        });
      } catch (error) {
        this.$message.error({
          message: 'Failed to send reminder',
        });
      } finally {
        this.$set(signer, 'sendingReminder', false);
      }
    },
    downloadAgreement(lease) {
      if (lease.agreementOnS3) {
        const link = document.createElement('a');
        link.href = lease.agreementOnS3;
        link.download = `lease-agreement-${lease._id}.pdf`;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
      }
    },
    formatCurrency(amount) {
      return this.$options.filters.currencySign(Number(amount), this.$root.state.facilityData);
    },
    agreementStatus(invoice) {
      if (invoice.signers && invoice.signers.length) {
        switch (true) {
          case (invoice.signers[0].status !== 'completed'):
            return 'Waiting for customer';
          case (invoice.signers[0].status === 'completed' && invoice.signers[1].status !== 'completed'):
            return 'Waiting for manager';
          case (invoice.signers[0].status === 'completed' && invoice.signers[1].status === 'completed'):
            return 'Completed';
          default:
            return '';
        }
      } else {
        return '';
      }
    },
    async agreementConfirmed(etchPacket) {
      this.loading = true;
      this.managerSignatureUrl = JSON.stringify(etchPacket);
      await this.linkAgreementToLease(this.managerSignatureUrl);
      await this.getSignersStatus(this.leaseSelectedForAgreement);
      this.licenseAgreementDialog = false;
      this.leaseSelectedForAgreement = null;
      this.activeNames = [];

      // await this.getCustomerInvoices();
      this.$emit('reload');
      this.loading = false;
    },
    async openLicenseAgreementDialog(lease) {
      this.leaseSelectedForAgreement = lease;
      this.licenseAgreementDialog = true;
    },
    async addCustomerAgreement(invoice, response) {
      this.loading = true;
      await axios.put(
        `${LEASING_API}/v1/${this.facility}/${invoice}/manual/agreement`,
        {
          agreementSigned: true,
          url: response.response.url,
          createdBy: this.$keycloak.tokenParsed.email,
        },
        {
          headers: {
            Authorization: `Bearer ${this.$keycloak.token}`,
          },
        },
      );
      this.$emit('reload');
      this.loading = false;
    },
    async cancelLicenseAgreement(lease) {
      const agreementString = lease.agreement;
      const agreement = JSON.parse(agreementString);
      const rsAnvilClient = new RsAnvilApi(this.$keycloak.token);

      await rsAnvilClient.cancelAgreement(agreement.eid, agreement.documentGroup.eid);
      await this.unlinkAgreementToLease(lease, agreement);
    },
    async unlinkAgreementToLease(lease, agreement) {
      try {
        this.loading = true;
        lease.events.push({
          type: 'agreement_cancelled',
          agreement,
          agreementOnS3: lease.agreementOnS3,
        });
        await axios.put(
          `${LEASING_API}/v1/${this.facility}/${lease._id}`,
          {
            agreement: '',
            agreementOnS3: '',
            agreementSigned: false,
            events: lease.events,
          },
          {
            headers: {
              Authorization: `Bearer ${this.$keycloak.token}`,
            },
          },
        );

        this.$emit('reload');

        this.loading = false;
        this.$notify.success({
          title: 'Success',
          message: 'Agreement cancelled succesfully',
        });
      } catch (err) {
        this.loading = false;
        this.$notify.error({
          title: 'Error',
          message: err.response.data,
        });
      }
    },
    async sendReminderEmail(invoice) {
      const agreementString = invoice.agreement;
      const agreement = JSON.parse(agreementString);
      const rsAnvilClient = new RsAnvilApi(this.$keycloak.token);

      await rsAnvilClient.sendReminderToSigners(agreement.eid);
      this.$message.success({
        message: 'Sent email reminder to signer(s).',
      });
    },
    async linkAgreementToLease(agreement) {
      try {
        this.loading = true;
        await axios.put(
          `${LEASING_API}/v1/${this.facility}/${this.leaseSelectedForAgreement._id}`,
          {
            agreement,
          },
          {
            headers: {
              Authorization: `Bearer ${this.$keycloak.token}`,
            },
          },
        );

        this.$emit('reload');
        this.loading = false;
        this.$notify.success({
          title: 'Success',
          message: agreement ? 'Agreement linked successfully.' : 'Agreement cancelled succesfully',
        });
      } catch (err) {
        this.loading = false;
        this.$notify.error({
          title: 'Error',
          message: err.response.data,
        });
      }
    },
    allowAccess(role) {
      return allowAccess(role, this.$keycloak, this.$root.state.facility);
    },
    lineItemBackgroundColor(lineItem) {
      if (lineItem.voided) {
        return 'linear-gradient(35deg, #ff9966, #ff5e62)';
      } else if (lineItem.type === 'DEBIT' && lineItem.transactionType !== 'ADJUSTMENT') {
        return 'linear-gradient(35deg, #4B8673, #0f9b0f)';
      } else if (lineItem.details === 'TAX') {
        return 'linear-gradient(35deg, #AF0171, #790252, #E80F88)';
      } else if (lineItem.details === 'RENT') {
        return 'linear-gradient(35deg, #AF0171, #790252, #E80F88)';
      } else if (lineItem.details === 'FEES') {
        return 'linear-gradient(35deg, #AF0171, #790252, #E80F88)';
      } else if (lineItem.transactionType === 'ADJUSTMENT') {
        return 'linear-gradient(35deg, #F2CD5C, #A61F69, #F2921D)';
      }
      return 'linear-gradient(35deg, #009aca, #0c3da7)';
    },
    getMonthValue(value) {
      return getMonth(value);
    },
    async getUnitRentals(lease) {
      try {
        this.loading = true;
        const unitRentals = await axios.get(`${UNIT_RENTAL_API_BASE}`, {
          headers: {
            Authorization: `Bearer ${this.$keycloak.token}`,
          },
          params: {
            ...(lease ? { unitInvoice: lease } : {}),
          },
        });
        this.unitRentals = [...this.unitRentals, ...unitRentals.data.result];
        this.loading = false;
      } catch (err) {
        console.log(err);
        this.loading = false;
        this.$notify.error({
          title: 'Error',
          message: err.response.data,
        });
      }
    },
    async getSignersStatus(invoice) {
      const lease = invoice;
      const rsAnvilClient = new RsAnvilApi(this.$keycloak.token);
      try {
        const document = lease && lease.agreement ? JSON.parse(lease.agreement) : null;
        if (document && document.eid) {
          invoice.signersLoading = true;
          this.signersStatusLoading = true;
          const response = await rsAnvilClient.getSignersStatus(document.eid);
          const signers = response.data.data.etchPacket.documentGroup.signers;
          signers.forEach((item, index) => {
            // eslint-disable-next-line no-param-reassign
            item.computedStatus = index === 0 ? signers[0].status : this.agreementStatus(invoice);
          });

          // eslint-disable-next-line no-param-reassign
          invoice.signers = signers;
          // eslint-disable-next-line no-param-reassign
          invoice.signersLoading = false;

          this.signersStatus = invoice.signers;
          this.signersStatusLoading = false;
          return response;
        }
      } catch (err) {
        console.log(err);
        this.signersStatusLoading = false;
      }

      // eslint-disable-next-line no-param-reassign
      invoice.signersLoading = false;
      return null;
    },
    cleanupTransferState() {
      this.transferCreationStatus = 'idle';
      this.transferProgress.current = 0;
      this.transferProgress.total = 0;
      this.transferProgress.message = 'Transfer was cancelled';
      this.wasJustCompleted = false;

      this.unitRentals = [];
      console.log('Transfer state cleaned up - transfer was cancelled');
    },

    handleTransferCancelled() {
      this.handleTransferCancellation();
      
      this.$notify.info({
        title: 'Info',
        message: 'Lease transfer has been cancelled.',
      });
    },
    async removeStage(stage) {
      this.loading = true;
      try {
        await axios.put(`${LEASE_TRANSFER_API}/v1/${this.facility}/${stage.lease}/${this.leaseTransfer.transfer._id}/remove/stage/${stage._id}`, {
          removeEndDateForActiveStage: true,
        }, {
          headers: {
            Authorization: `Bearer ${this.$keycloak.token}`,
          },
        });
      } catch (error) {
        this.$message.error({
          message: 'Failed to remove stage.',
        });
      } finally {
        this.loading = false;
        this.$message.success({
          message: 'Stage removed successfully.',
        });
        this.$emit('reload');
      }
    },
  },
};
</script>

<style scoped>
.stage-title-content {
  display: flex;
  justify-content: space-between;
  align-items: center;
  width: 100%;
}

.stage-basic-info {
  display: flex;
  flex-wrap: wrap;
  gap: 10px;
}

.amount-transferable {
  margin-left: 15px;
}

.amount-transferable .el-tag {
  font-weight: bold;
}

.next-stage-info {
  margin-top: 15px;
  padding: 12px;
  background-color: #f8f9fa;
  border-radius: 6px;
  border: 1px solid #e4e7ed;
}

.next-stage-info h6 {
  margin: 0 0 10px 0;
  color: #303133;
  font-size: 14px;
  font-weight: 600;
}

.next-stage-details {
  padding: 0;
}

.amount-breakdown {
  margin-bottom: 12px;
}

.breakdown-item {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 6px 0;
  border-bottom: 1px solid #f0f0f0;
  font-size: 13px;
}

.breakdown-item:last-child {
  border-bottom: none;
}

.breakdown-item.total {
  font-weight: bold;
  font-size: 14px;
  padding: 8px 0;
  border-top: 2px solid #409eff;
  border-bottom: none;
}

.breakdown-item .label {
  color: #606266;
  font-weight: 500;
}

.breakdown-item .value {
  font-weight: 600;
  font-size: 13px;
}

.breakdown-item .value.amount-due {
  color: #e6a23c;
}

.breakdown-item .value.transferable {
  color: #67c23a;
}

.breakdown-item .value.wallet-balance {
  color: #409eff;
}

.breakdown-item .value.ach-pending {
  color: #e6a23c;
}

.breakdown-item .value.actual-amount {
  color: #f56c6c;
  font-size: 14px;
}

.next-stage-summary {
  margin-top: 10px;
}

.next-stage-summary .el-alert {
  margin: 0;
  padding: 8px 12px;
}

.next-stage-summary .el-alert__content {
  padding: 0;
}

.next-stage-summary .el-alert__title {
  font-size: 13px;
  line-height: 1.3;
}

.next-stage-summary span {
  font-size: 12px;
  line-height: 1.3;
}

.wallet-info {
  margin-top: 10px;
}

.wallet-info .el-alert {
  margin: 0;
  padding: 6px 10px;
}

.wallet-info .el-alert__content {
  padding: 0;
}

.wallet-info .el-alert__title {
  font-size: 12px;
  line-height: 1.2;
}

.wallet-info span {
  font-size: 11px;
  line-height: 1.2;
}

/* Signers Dialog Styles */
.signers-dialog .el-dialog__body {
  padding: 20px;
}

.loading-container {
  padding: 20px;
}

.signers-content {
  display: flex;
  flex-direction: column;
  gap: 20px;
}

.signers-table-section {
  margin-bottom: 20px;
}

.table-card {
  border: 1px solid #e4e7ed;
  border-radius: 8px;
}

.table-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  font-weight: 600;
  color: #303133;
}

.header-left {
  display: flex;
  align-items: center;
  gap: 8px;
}

.header-left i {
  color: #409eff;
}

.refresh-btn {
  margin-left: auto;
}

.signers-table {
  margin-top: 16px;
}

.signer-info {
  display: flex;
  flex-direction: column;
  gap: 4px;
}

.signer-name {
  display: flex;
  align-items: center;
  gap: 8px;
  font-weight: 600;
  color: #303133;
}

.signer-name i {
  color: #409eff;
}

.signer-email {
  font-size: 12px;
  color: #606266;
}

.signed-date {
  font-size: 12px;
  color: #67c23a;
  font-weight: 500;
}

.not-signed {
  color: #c0c4cc;
  font-style: italic;
}

.no-action {
  color: #c0c4cc;
  font-style: italic;
}

/* Enhanced table styling */
.signers-table .el-table__row:hover {
  background-color: #f5f7fa;
}

.signers-table .el-table__header {
  background-color: #fafafa;
}

.signers-table .el-table__header th {
  background-color: #fafafa;
  color: #606266;
  font-weight: 600;
}


.no-signers {
  padding: 40px 20px;
  text-align: center;
}

/* Table row styling */
.signers-table .completed-row {
  background-color: #f0f9ff;
}

.signers-table .pending-row {
  background-color: #fef7e0;
}

.signers-table .declined-row {
  background-color: #fef0f0;
}

/* Responsive design */
@media (max-width: 768px) {
  .signers-dialog .el-dialog {
    width: 95% !important;
    margin: 0 auto;
  }
}

.stage-header-content {
  display: flex;
  justify-content: space-between;
  align-items: center;
  width: 100%;
}

.stage-header-content h5 {
  margin: 0;
  color: #303133;
  font-size: 16px;
}

.stage-actions {
  display: flex;
  gap: 6px;
  flex-wrap: wrap;
  justify-content: flex-end;
  padding: 8px 0;
}

.stage-actions .el-button {
  font-size: 12px;
  padding: 6px 12px;
  height: auto;
  line-height: 1.2;
}

.stage-actions .el-button .el-icon-refresh {
  margin-right: 4px;
}

.stage-actions .el-button .el-icon-bell {
  margin-right: 4px;
}

.stage-actions .el-button .el-icon-download {
  margin-right: 4px;
}

.transfer-progress {
  margin-bottom: 15px;
  padding: 12px;
  background-color: #f8f9fa;
  border-radius: 6px;
  border: 1px solid #e4e7ed;
}

.progress-alert {
  margin: 0;
  padding: 8px 12px;
}

.progress-content {
  display: flex;
  align-items: center;
  gap: 10px;
  margin-top: 8px;
}

.progress-text {
  font-size: 13px;
  color: #606266;
  white-space: nowrap;
}

.transfer-success {
  margin-bottom: 15px;
  padding: 12px;
  background-color: #f8f9fa;
  border-radius: 6px;
  border: 1px solid #e4e7ed;
}

.transfer-success .el-alert {
  margin: 0;
  padding: 8px 12px;
}

.transfer-success .el-alert__content {
  padding: 0;
}

.transfer-success .el-alert__title {
  font-size: 14px;
  line-height: 1.3;
}

.transfer-success span {
  font-size: 12px;
  line-height: 1.3;
}

.agreement-group {
  display: flex;
  align-items: center;
  gap: 6px;
  padding: 4px 8px;
  background-color: #f8f9fa;
  border-radius: 4px;
  border: 1px solid #e4e7ed;
}

.group-label {
  font-size: 11px;
  font-weight: 600;
  color: #606266;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  margin-right: 4px;
}

.agreement-group .el-button {
  font-size: 11px;
  padding: 6px 10px;
  height: 28px;
  line-height: 1.1;
  min-width: 60px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
}

.agreement-group .el-button .el-icon-refresh {
  margin-right: 0;
}

.agreement-group .el-button .el-icon-bell {
  margin-right: 0;
}

.agreement-group .el-button .el-icon-download {
  margin-right: 0;
}

/* Override Uploader button styling to match */
.agreement-group .el-upload {
  display: inline-block;
}

.agreement-group .el-upload .el-button {
  font-size: 11px;
  padding: 6px 10px;
  height: 28px;
  line-height: 1.1;
  min-width: 60px;
}

.custom-agreement-group {
  display: flex;
  align-items: center;
  gap: 6px;
  padding: 4px 8px;
  background-color: #fff3cd;
  border-radius: 4px;
  border: 1px solid #ffeaa7;
  margin-left: 8px;
}

.custom-agreement-group .group-label {
  font-size: 11px;
  font-weight: 600;
  color: #856404;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  margin-right: 4px;
}

.custom-agreement-group .el-button {
  font-size: 11px;
  padding: 6px 10px;
  height: 28px;
  line-height: 1.1;
  min-width: 60px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
}

.custom-agreement-group .el-upload {
  display: inline-block;
}

.custom-agreement-group .el-upload .el-button {
  font-size: 11px;
  padding: 6px 10px;
  height: 28px;
  line-height: 1.1;
  min-width: 60px;
}

.cancel-stage-btn {
  font-weight: 600;
  border-radius: 6px;
  box-shadow: 0 2px 4px rgba(245, 108, 108, 0.2);
  transition: all 0.3s ease;
  position: relative;
  overflow: hidden;
}

.cancel-stage-btn:hover {
  transform: translateY(-1px);
  box-shadow: 0 4px 8px rgba(245, 108, 108, 0.3);
  background-color: #f56c6c;
  border-color: #f56c6c;
}

.cancel-stage-btn:active {
  transform: translateY(0);
  box-shadow: 0 2px 4px rgba(245, 108, 108, 0.2);
}

.cancel-stage-btn .el-icon-delete {
  margin-right: 4px;
  font-size: 12px;
}

.cancel-stage-btn::before {
  content: '';
  position: absolute;
  top: 0;
  left: -100%;
  width: 100%;
  height: 100%;
  background: linear-gradient(90deg, transparent, rgba(255, 255, 255, 0.2), transparent);
  transition: left 0.5s;
}

.cancel-stage-btn:hover::before {
  left: 100%;
}

.stage-content-layout {
  display: grid;
  grid-template-columns: 2fr 1fr;
  gap: 20px;
  margin-top: 15px;
}

.billing-section h6,
.agreement-section h6 {
  margin: 0 0 15px 0;
  color: #303133;
  font-size: 14px;
  font-weight: 600;
  padding-bottom: 8px;
  border-bottom: 1px solid #e4e7ed;
}

.agreement-status {
  margin-bottom: 15px;
}

.signers-info {
  margin-top: 10px;
}

.signers-info p {
  margin: 5px 0;
  font-weight: bold;
  font-size: 12px;
}

.signers-info ul {
  margin: 5px 0;
  padding-left: 20px;
  font-size: 12px;
}

.signers-info li {
  margin: 3px 0;
}

.no-agreement {
  margin-top: 10px;
}

/* Responsive design for smaller screens */
@media (max-width: 768px) {
  .stage-content-layout {
    grid-template-columns: 1fr;
    gap: 15px;
  }

  .stage-header-content {
    flex-direction: column;
    gap: 10px;
    align-items: flex-start;
  }

  .stage-actions {
    justify-content: flex-start;
  }
}
</style>



// WEBPACK FOOTER //
// src/components/LeaseTransferView.vue